import { CommonModule } from '@angular/common';
import {
    Component,
    InputSignal,
    OnInit,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    input,
    signal,
} from '@angular/core';
import {
    ColumnKeys,
    UpsellColumnKeys,
    ColumnOrder,
    UpsellColumnOrder,
    DataRows,
    RowKeys,
    RowOrder,
    Rows,
    TrendlinesSnapshotResponse,
    emptyDataRows,
    emptyRows,
    getCardMonthLabel,
    getCardWeekLabel,
    getCardYearLabel,
    getEightDayForecastUpsellCardLabel,
    getEightDayForecastUpsellLabel,
    getMonthLabel,
    getThirtyFiveDayForecastUpsellCardLabel,
    getThirtyFiveDayForecastUpsellLabel,
    getWeekLabel,
    getYearLabel,
} from '../models';

import { CargoMdDirective } from '@dat/cargo-template';
import { isNil, kebabCase } from 'lodash';
import { IqTrendlinesSnapshotStateService } from '../state';
import { IqTrendlinesFfStateService } from '@dat/iq-trendlines-ff';

@Component({
    selector: 'iq-trendlines-snapshot-table',
    standalone: true,
    imports: [CommonModule, CargoMdDirective],
    templateUrl: './iq-trendlines-snapshot-table.component.html',
    styleUrl: './iq-trendlines-snapshot-table.component.scss',
})
export class IqTrendlinesSnapshotTableComponent implements OnInit {
    id: InputSignal<string> = input('');
    testId: InputSignal<string> = input('');

    data: WritableSignal<DataRows> = signal(emptyDataRows);

    colOrder: ColumnKeys[] = [...ColumnOrder];
    rowOrder: RowKeys[] = [...RowOrder];

    colOrderSignal: Signal<ColumnKeys[] | UpsellColumnKeys[]> = computed(() => {
        const isUpsellEnabled = this.upsellEnabled();
        const response = this.response();
        return !isNil(response) && isUpsellEnabled? [...UpsellColumnOrder] : [...ColumnOrder];
    })

    private _ff: IqTrendlinesFfStateService = inject(
        IqTrendlinesFfStateService,
    );

    upsellEnabled: Signal<boolean> = computed(() => {
        return this._ff.isFlagEnabledSig('trendlines_upsell')();
    });

    currentTestId: Signal<string> = computed(() => {
        return this.testId()?.length ? this.testId() : this.id();
    });

    headerRow: {
        [key in ColumnKeys]: { id: string; label: string; cardLabel: string };
    } = {
        Trends: {
            id: kebabCase('Trends'),
            label: 'INDUSTRY TRENDS',
            cardLabel: 'INDUSTRY TRENDS',
        },
        WeekComparison: {
            id: kebabCase('WeekComparison'),
            label: getWeekLabel(),
            cardLabel: getCardWeekLabel(),
        },
        MonthComparison: {
            id: kebabCase('MonthComparison'),
            label: getMonthLabel(),
            cardLabel: getCardMonthLabel(),
        },
        YearComparison: {
            id: kebabCase('YearComparison'),
            label: getYearLabel(),
            cardLabel: getCardYearLabel(),
        },
    };

    upsellHeaderRow: {
        [key in UpsellColumnKeys]: { id: string; label: string; cardLabel: string };
    } = {
        ...this.headerRow,
        EightDayForecastUpsell: {
            id: kebabCase('EightDayForecastUpsell'),
            label: getEightDayForecastUpsellLabel(),
            cardLabel: getEightDayForecastUpsellCardLabel(),
        },
        ThirtyFiveDayForecastUpsell: {
            id: kebabCase('ThirtyFiveDayForecastUpsell'),
            label: getThirtyFiveDayForecastUpsellLabel(),
            cardLabel: getThirtyFiveDayForecastUpsellCardLabel(),
        },
    };

    headerRowSignal: Signal<{ [key in ColumnKeys | UpsellColumnKeys]?: { id: string; label: string; cardLabel: string } }> = computed(() => {
        const isUpsellEnabled = this.upsellEnabled();
        return isUpsellEnabled ? this.upsellHeaderRow : this.headerRow;
    });

    rowsSignal: WritableSignal<Rows> = signal(emptyRows);
    rows: Signal<Rows> = computed((): Rows => {
        return this.rowsSignal();
    });
    response: WritableSignal<TrendlinesSnapshotResponse> = signal(null);
    // Call this in the OnInit Function
    private _setRowsSignal(response: TrendlinesSnapshotResponse, isUpsellEnabled: boolean = false): void {
        this.rowsSignal.set(this._state.rows(response, isUpsellEnabled)());
    }

    constructor(private _state: IqTrendlinesSnapshotStateService) {
        this._state.getRows().then((response) => {
            this.response.set(response);
            this.data.set(this._state.responseMapper(response));
        });
        effect(
            () => {
                if (!isNil(this.response())) {
                    this._setRowsSignal(this.response(), this.upsellEnabled());
                }
            },
            {
                allowSignalWrites: true,
            },
        );
    }

    ngOnInit() {
        this._setRowsSignal(this.response());
    }

    sendPayloadToIframe(redirectUrl: string) {
        if (window.self !== window.top) {
            window.parent.postMessage(
                {
                    type: 'IFRAME_NAVIGATE_URL',
                    payload: {
                        url: redirectUrl,
                    },
                },
                '*',
            );
        }
    }

    dispacthSegmentEvent(linkLabel: string): void {
        this._state.dipstachUpsellSegmentEvent(linkLabel);
    }
}
